import { useState, useEffect, useRef } from 'react';

import { useCurrentAircraft, useAnalytics } from 'ff/debrief/hooks';
import { useDebriefModal } from 'ff/debrief/modalManager';
import {
    VspeedFormValues,
    SubmitHandler,
    VSPEED_FIELDS,
    VSpeedField,
} from './VspeedFormValues';
import { AircraftProfile, DebriefAircraft, Vspeeds } from 'ff/debrief/models';

const createVSpeedValues = (overrides?: Partial<Vspeeds> | null) => {
    return Object.fromEntries(
        VSPEED_FIELDS.map(field => [field, overrides?.[field]])
    ) as Record<VSpeedField, number | undefined>;
};

const updateVSpeedFields = (
    setFieldValue: (field: string, value: unknown) => void,
    overrides: Partial<Vspeeds> | null
) => {
    VSPEED_FIELDS.forEach(field => setFieldValue(field, overrides?.[field]));
};

export const useVspeedModalLogic = () => {
    const { logAircraftConfigChanged } = useAnalytics();
    const { isOpen, close } = useDebriefModal('Vspeed');
    const {
        vSpeedOverrides,
        standardSpeeds,
        currentAircraft,
        setOverrides,
        currentTailNumber,
        updateTailNumber,
        setAircraftModel,
        getVspeedsForTail,
        getAircraftListForTail,
        speedUnit,
    } = useCurrentAircraft();

    const [modelList, setModelList] = useState<DebriefAircraft[]>([]);
    const [aircraftProfile, setAircraftProfile] =
        useState<AircraftProfile>(null);
    const [initialValues, setInitialValues] = useState<VspeedFormValues | null>(
        null
    );
    const isInitialized = useRef(false);

    useEffect(() => {
        if (isOpen && !isInitialized.current) {
            setInitialValues({
                ...createVSpeedValues(vSpeedOverrides),
                tailNumber: currentTailNumber,
                aircraftModel: currentAircraft,
                preferredSpeedUnit: speedUnit || 'kts',
            });
            isInitialized.current = true;
        } else if (!isOpen) {
            isInitialized.current = false;
            setInitialValues(null);
        }
    }, [
        isOpen,
        currentTailNumber,
        currentAircraft,
        vSpeedOverrides,
        speedUnit,
    ]);

    const onTailChange = async (
        values: VspeedFormValues,
        setFieldValue?: (field: string, value: unknown) => void
    ) => {
        const newTailObject = values.tailNumber;
        if (!newTailObject || newTailObject.tailNumber === 'Unknown') return;

        const [profile, modelList] = await Promise.all([
            getVspeedsForTail(newTailObject.tailNumber),
            getAircraftListForTail(newTailObject.tailNumber),
        ]);

        setAircraftProfile(profile);
        setModelList(modelList);

        const aircraftModel = {
            manufacturer: profile.manufacturer,
            model: profile.model,
            label: `${profile.manufacturer} / ${profile.model}`,
        };

        if (setFieldValue) {
            setFieldValue('aircraftModel', aircraftModel);
            updateVSpeedFields(setFieldValue, profile.aircraftVSpeed);
        }

        return aircraftModel;
    };

    const onAircraftModelChange = async (
        values: VspeedFormValues,
        setFieldValue?: (field: string, value: unknown) => void
    ) => {
        const newAircraft = values.aircraftModel;
        const currTail = values.tailNumber;
        if (!newAircraft || !currTail) return;

        const newProfile = await getVspeedsForTail(
            currTail.tailNumber,
            newAircraft.manufacturer,
            newAircraft.model
        );
        setAircraftProfile(newProfile);

        if (setFieldValue) {
            updateVSpeedFields(setFieldValue, newProfile.aircraftVSpeed);
        }
    };

    const onSubmit: SubmitHandler = async (values, helpers) => {
        const {
            tailNumber,
            aircraftModel,
            preferredSpeedUnit,
            ...vspeedValues
        } = values;

        const cleanedValues = Object.fromEntries(
            Object.entries(vspeedValues).map(([key, val]) => [key, val || null])
        ) as Omit<
            VspeedFormValues,
            'tailNumber' | 'aircraftModel' | 'preferredSpeedUnit'
        >;

        try {
            await setOverrides(
                cleanedValues,
                preferredSpeedUnit,
                tailNumber.tailNumber
            );

            const tailChanged =
                tailNumber &&
                tailNumber.tailNumber !== currentTailNumber?.tailNumber;
            const aircraftChanged =
                aircraftModel && aircraftModel !== currentAircraft;
            const vspeedChanged = Object.entries(cleanedValues).some(
                ([key, newValue]) =>
                    newValue !==
                    vSpeedOverrides?.[key as keyof typeof cleanedValues]
            );

            if (tailChanged) await updateTailNumber(tailNumber.tailNumber);
            if (aircraftChanged) {
                await setAircraftModel(aircraftModel, tailNumber.tailNumber);
            }

            logAircraftConfigChanged({
                tailChanged: tailChanged ? 'yes' : 'no',
                aircraftChanged: aircraftChanged ? 'yes' : 'no',
                vspeedChanged: vspeedChanged ? 'yes' : 'no',
            });
        } finally {
            helpers.setSubmitting(false);
            close();
        }
    };

    const fallbackInitialValues: VspeedFormValues = {
        ...createVSpeedValues(null),
        tailNumber: currentTailNumber,
        aircraftModel: currentAircraft,
        preferredSpeedUnit: speedUnit || 'kts',
    };

    return {
        isOpen,
        close,
        standardSpeeds,
        modelList,
        initialValues: initialValues || fallbackInitialValues,
        aircraftProfile: aircraftProfile,
        handleSubmit: onSubmit,
        handleTailChange: onTailChange,
        handleAircraftModelChange: onAircraftModelChange,
    };
};
